import React, { ReactNode, useState } from 'react';
import { ErrorOutlineOutlined, ExpandMore, TimerSharp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Stage, TaskItem } from '../../taskList/interfaces';
import { FORMS, TaskItemStatus } from '../constants/constants';
import { SessionStore } from '../../constants';
import { BasicTaskList } from '../styles/BasicTaskList';
import BasicProgressBar from './BasicProgressBar';
import { initializeTask } from '../../taskList/utils/initializeTask';
import { ReactComponent as LockTileIcon } from '../images/lock_icon.svg';
import { ReactComponent as CompletedTaskIcon } from '../../taskList/images/completed_task.svg';
import { formsIndicators } from '../utils/checkFormsCompleted';
import { useAppSelector } from '../../store/hook';
import { ResponseReuseType } from '../../taskList/constants';
import Alert from '../../common/components/Alert';
import { WCAGStrandardStyles } from '../../common/styles/WCAGStandardStyles';
import RRMessageLabel from './RRMessageLabel';
import { AccessibilityLabel } from './../../common/components/AccessibilityLabel';

interface StageSectionProps {
  data: Stage[];
  onRRMsgBtnClick?: (showPopup: boolean, currentItem?: TaskItem) => void;
}
const StageSection = (props: StageSectionProps): React.JSX.Element => {
  const { data, onRRMsgBtnClick } = props;
  const { accessibilityStatementEnabled } = useAppSelector((state) => state.companySettings);
  const { settings } = useAppSelector((state) => state.template);
  const { userSessionActive } = useAppSelector((state) => state.basicDetails);
  const candidate = useAppSelector((state) => state.candidate);
  const { isICPEnabled } = useAppSelector((state) => state.headerDetails);
  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const basicTaskListHurdleIndicator = sessionStorage.getItem(SessionStore.basicTaskListHurdleIndicator) === 'true';

  const [alertVisible, setAlertVisible] = useState<boolean>(basicTaskListHurdleIndicator);

  const formStatusSession = sessionStorage.getItem(SessionStore.formStatus);
  const formStatus = formStatusSession && JSON.parse(formStatusSession);
  const indicatorObj = formsIndicators();
  const { classes } = BasicTaskList({ isICPEnabled: isICPEnabled, settings: settings });
  const { classes: WCAGClasses } = WCAGStrandardStyles();
  const onStageItemClick = async (item: TaskItem, stage: Stage): Promise<void> => {
    if (item.code === FORMS) {
      navigate(`${item.link}`);
    } else {
      await initializeTask(stage, userSessionActive, navigate, location, candidate, null, item, currentWorkflowItem);
    }
  };
  const onStageItemKeyDownClick = async (
    event: React.KeyboardEvent<HTMLButtonElement>,
    item: TaskItem,
    stage: Stage,
  ): Promise<void> => {
    if (event.keyCode === 13 || event.keyCode === 32) {
      onStageItemClick(item, stage);
    }
  };
  const handleAlertHide = (): void => {
    sessionStorage.setItem(SessionStore.basicTaskListHurdleIndicator, 'false');
    setAlertVisible(false);
  };

  const checkStart = (item: TaskItem, stage: Stage): ReactNode => {
    const hasExhausted = item.state && JSON.parse(item.state)?.hasExhausted;
    if (
      (item.completionIndicator && formStatus && formStatus[item.completionIndicator]) ||
      item.status === TaskItemStatus.COMPLETED
    ) {
      return (
        <Box
          className={`${classes.floatRight}`}
          title={t('taskTileCompletedStatus')}
          aria-label={t('taskTileCompletedStatus')}>
          <CompletedTaskIcon className={`${classes.completedIcon}`} />
        </Box>
      );
    } else if ((item.isActive || !stage.isSequentialFlow) && item.status === TaskItemStatus.IN_PROGRESS) {
      return (
        <div title={hasExhausted ? t('exitEntryLimitExhaust') : ''}>
          <Button
            data-testid="continueButton"
            aria-label={t('continue')}
            className={classes.fullLengthButton}
            disabled={hasExhausted}
            variant="outlined"
            color="primary"
            onClick={(): Promise<void> => onStageItemClick(item, stage)}
            onKeyDown={(event): Promise<void> => onStageItemKeyDownClick(event, item, stage)}>
            {t('continue')}
          </Button>
        </div>
      );
    } else if (
      (item.code === FORMS &&
        item.completionIndicator &&
        formStatus &&
        !formStatus[item.completionIndicator] &&
        item.isActive &&
        item.link) ||
      (item.code !== FORMS && item.status === TaskItemStatus.NOT_STARTED && (item.isActive || !stage.isSequentialFlow))
    ) {
      return (
        <Button
          className={classes.fullLengthButton}
          aria-label={t('taskItemStartButtonText')}
          data-testid="startButton"
          variant="outlined"
          color="primary"
          onClick={(): Promise<void> => onStageItemClick(item, stage)}
          onKeyDown={(event): Promise<void> => onStageItemKeyDownClick(event, item, stage)}>
          <div>{t('taskItemStartButtonText')}</div>
        </Button>
      );
    } else if (stage.isSequentialFlow || item.code === FORMS) {
      return (
        <Box
          className={`${classes.floatRight}`}
          title={t('taskTileLockedStatus')}
          aria-label={t('taskTileLockedStatus')}>
          <LockTileIcon className={`${classes.lock}`} />
        </Box>
      );
    } else return null;
  };

  const firstStageIndex: number =
    data.length && data[0].title && data[0].title.toString().toUpperCase() === FORMS ? 1 : 0;
  const isHurdled: boolean = data.length > 0 && data.filter((stage) => stage.hurdleType != null).length > 0;

  const projectSettings = useAppSelector((state) => state.projectSettings);
  const showSubsequentStages = projectSettings.showSubsequentStages;

  const fetchStageTitle = (stage: Stage, index: number): string => {
    if (stage.title?.toString().toUpperCase() === FORMS) return t(stage.title);
    if (!stage.title)
      return data.length === 1 + firstStageIndex ? t(`yourTasks`) : t(`stage${index + 1 - firstStageIndex}`);
    return stage.title;
  };

  return (
    <>
      <Box className={classes.accordionWrapper}>
        {data.map((stage, index: number) => (
          <>
            {(stage.visibility || stage.title?.toString().toUpperCase() === FORMS) && (
              <>
                {index === firstStageIndex && isHurdled && (
                  <Alert
                    className={classes.basicTaskListMessage}
                    show={alertVisible}
                    state="info"
                    icon={<ErrorOutlineOutlined />}
                    onClose={handleAlertHide}>
                    {t('basicTaskListAlert', {
                      taskCount: showSubsequentStages ? data.length - firstStageIndex : 'n',
                    })}
                  </Alert>
                )}
                <Accordion
                  key={stage.title}
                  role="region"
                  aria-labelledby={`accordion${index}`}
                  variant="outlined"
                  className={`${classes.accordion} ${
                    stage.completed || stage.locked ? classes.stageDisabledColor : ''
                  }`}
                  disabled={stage.locked}
                  defaultExpanded={stage.isActive}>
                  <AccordionSummary
                    expandIcon={stage.locked ? <LockTileIcon className={`${classes.lock}`} /> : <ExpandMore />}
                    className={`${classes.accordionSummary} ${classes.positionRelative}`}>
                    <Box flex={1} className={classes.width100}>
                      <span id={`accordion${index}`} className={classes.cardTitle}>
                        {fetchStageTitle(stage, index)}
                      </span>
                      <BasicProgressBar data={stage.items} />
                    </Box>
                    <Box flex={1} className={`${classes.width100} ${classes.textAlignRight}`}>
                      {!stage.completed ? (
                        stage.title?.toString().toUpperCase() !== FORMS && (
                          <>
                            <TimerSharp className={classes.timerIcon} />
                            <span className={classes.timeValue}>{stage.duration}</span>
                            <span className={classes.description}> {t('minutes')}</span>
                          </>
                        )
                      ) : (
                        <>
                          <CompletedTaskIcon className={`${classes.completedIcon} ${classes.verticalAlignTop}`} />
                          <span className={classes.completedTitle}>{t('taskTileCompletedStatus')}</span>
                        </>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {stage.items.map((item: TaskItem) =>
                      (item.code === FORMS && item.indicator && indicatorObj[item.indicator]) || item.code !== FORMS ? (
                        <Paper key={item.name} square={false} className={classes.card} variant="outlined">
                          <Box className={classes.accordionDetailBox}>
                            {smUp ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                className={!item.isActive ? classes.defaultCursor : ''}>
                                {item.code !== FORMS && (
                                  <Box flex="15%" className={classes.timerText}>
                                    <span className={classes.timeValue}>{item.duration}</span>
                                    <span className={classes.description}> {t('minutes')}</span>
                                  </Box>
                                )}

                                <Box flex="75%" className={item.code !== FORMS ? classes.cardContent : ''}>
                                  <Typography className={classes.taskTitle}>{t(`${item.name}`)}</Typography>
                                  <Typography className={classes.description}>{item.description}</Typography>
                                  {accessibilityStatementEnabled && (
                                    <AccessibilityLabel
                                      accessibilityFeatures={item.accessibilityFeatures}
                                      className={classes.accessibilityLabel}
                                      title={item.name || ''}
                                    />
                                  )}
                                  {item?.responseReuseType === ResponseReuseType.FULL_RR && (
                                    <RRMessageLabel
                                      onRRMsgBtnClick={onRRMsgBtnClick}
                                      item={item}
                                      className={classes.reponseReuseLabel}
                                    />
                                  )}
                                </Box>
                                <Box flex="10%">{checkStart(item, stage)}</Box>
                              </Box>
                            ) : (
                              <>
                                {item.code !== FORMS && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    className={`${classes.accordionContentPaddingMobile} ${classes.cardContentMobile}`}>
                                    <Box flex="50%" className={classes.timerText}>
                                      <span className={classes.timeValue}>{item.duration}</span>
                                      <span className={classes.description}> {t('minutes')}</span>
                                    </Box>
                                    {item?.responseReuseType === ResponseReuseType.FULL_RR && (
                                      <RRMessageLabel
                                        onRRMsgBtnClick={onRRMsgBtnClick}
                                        item={item}
                                        className={classes.reponseReuseLabel}
                                      />
                                    )}
                                  </Box>
                                )}
                                {(!item.isActive && (stage.isSequentialFlow || item.code === FORMS)) ||
                                (!stage.isSequentialFlow && item.status === TaskItemStatus.COMPLETED) ? (
                                  <Box display="flex" alignItems="center" justifyContent="center">
                                    <Box flex="75%" className={`${classes.accordionContentPaddingMobile}`}>
                                      <Typography className={classes.taskTitle}>{t(`${item.name}`)}</Typography>
                                      <Typography className={WCAGClasses.subtitle2}>{item.description}</Typography>
                                      {accessibilityStatementEnabled && (
                                        <AccessibilityLabel
                                          accessibilityFeatures={item.accessibilityFeatures}
                                          className={classes.accessibilityLabel}
                                          title={item.name || ''}
                                        />
                                      )}
                                    </Box>
                                    <Box>{checkStart(item, stage)}</Box>
                                  </Box>
                                ) : (
                                  <Box className={`${classes.accordionContentPaddingMobile}`}>
                                    <Typography className={classes.taskTitle}>{t(`${item.name}`)}</Typography>
                                    <Typography className={classes.description}>{item.description}</Typography>
                                    {accessibilityStatementEnabled && (
                                      <AccessibilityLabel
                                        accessibilityFeatures={item.accessibilityFeatures}
                                        className={classes.accessibilityLabel}
                                        title={item.name || ''}
                                      />
                                    )}
                                  </Box>
                                )}

                                {(item.isActive && stage.isSequentialFlow) ||
                                (item.isActive && item.code === FORMS) ||
                                (!stage.isSequentialFlow &&
                                  (item.status === TaskItemStatus.NOT_STARTED ||
                                    item.status === TaskItemStatus.IN_PROGRESS) &&
                                  item.code !== FORMS) ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    className={classes.accordionContentPaddingMobile}>
                                    <Box flex="50%" className={classes.alignRight}>
                                      {checkStart(item, stage)}
                                    </Box>
                                  </Box>
                                ) : (
                                  <></>
                                )}
                              </>
                            )}
                          </Box>
                        </Paper>
                      ) : (
                        ''
                      ),
                    )}
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </>
        ))}
      </Box>
    </>
  );
};

export default StageSection;
